/**
 * RTK Query endpoints for platform organization billing detail.
 * Covers: projected billing, billing account, subscriptions, packages, usage.
 *
 * Phase 1 of Q9b: endpoint definitions only. Hooks not yet consumed by page.
 */

import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse, unwrapListResponse } from '@/api/patterns/crud';
import type {
  OrgProjectedBilling,
  OrgBillingAccount,
  OrgSubscription,
  OrgPackageInfo,
  OrgUsageSummary,
  PauseChargingArgs,
  ResumeChargingArgs,
  ToggleChargingArgs,
  AddSubscriptionArgs,
  CancelSubscriptionArgs,
  TogglePauseSubscriptionArgs,
} from '../types';

export const billingDetailApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // ── Queries ─────────────────────────────────────────────────

    getOrgProjectedBilling: builder.query<OrgProjectedBilling, string>({
      query: (orgId) => `/admin/billing/projected/${orgId}`,
      transformResponse: unwrapApiResponse<OrgProjectedBilling>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),

    getOrgBillingAccount: builder.query<OrgBillingAccount, string>({
      query: (orgId) => `/admin/billing/accounts/${orgId}`,
      transformResponse: unwrapApiResponse<OrgBillingAccount>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgBillingAccount', id: orgId },
      ],
    }),

    getOrgSubscriptions: builder.query<OrgSubscription[], string>({
      query: (orgId) => `/admin/billing/subscriptions/${orgId}?includeInactive=true`,
      transformResponse: unwrapListResponse<OrgSubscription>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgSubscriptions', id: orgId },
      ],
    }),

    getAvailablePackages: builder.query<OrgPackageInfo[], void>({
      query: () => '/admin/billing/packages',
      transformResponse: (response: { data: OrgPackageInfo[] } | OrgPackageInfo[]) => {
        const items = unwrapListResponse<OrgPackageInfo>(response);
        return items.filter((p) => p.isActive);
      },
      // Global packages — not org-specific
      providesTags: [{ type: 'Packages', id: 'ACTIVE' }],
    }),

    getOrgUsageSummary: builder.query<OrgUsageSummary[], string>({
      query: (orgId) => `/admin/billing/usage/${orgId}`,
      transformResponse: unwrapListResponse<OrgUsageSummary>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgUsage', id: orgId },
      ],
    }),

    // ── Mutations ───────────────────────────────────────────────

    pauseOrgCharging: builder.mutation<OrgBillingAccount, PauseChargingArgs>({
      query: ({ organizationId }) => ({
        url: '/admin/billing/pause-charging',
        method: 'POST',
        body: { organizationId },
      }),
      transformResponse: unwrapApiResponse<OrgBillingAccount>,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'PlatformOrgBillingAccount', id: organizationId },
      ],
    }),

    resumeOrgCharging: builder.mutation<OrgBillingAccount, ResumeChargingArgs>({
      query: ({ organizationId }) => ({
        url: '/admin/billing/resume-charging',
        method: 'POST',
        body: { organizationId },
      }),
      transformResponse: unwrapApiResponse<OrgBillingAccount>,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'PlatformOrgBillingAccount', id: organizationId },
      ],
    }),

    toggleOrgCharging: builder.mutation<OrgBillingAccount, ToggleChargingArgs>({
      query: ({ organizationId, isCharging, stepUpToken, stepUpTimestamp }) => ({
        url: '/admin/billing/toggle-charging',
        method: 'POST',
        body: { organizationId, isCharging },
        headers: stepUpToken
          ? { 'X-Step-Up-Token': stepUpToken, ...(stepUpTimestamp && { 'X-Step-Up-Timestamp': stepUpTimestamp }) }
          : undefined,
      }),
      transformResponse: unwrapApiResponse<OrgBillingAccount>,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'PlatformOrgBillingAccount', id: organizationId },
      ],
    }),

    addOrgSubscription: builder.mutation<OrgSubscription, AddSubscriptionArgs>({
      query: ({ organizationId, packageId }) => ({
        url: '/admin/billing/subscriptions',
        method: 'POST',
        body: { organizationId, packageId },
      }),
      transformResponse: unwrapApiResponse<OrgSubscription>,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'PlatformOrgSubscriptions', id: organizationId },
        { type: 'PlatformOrgProjectedBilling', id: organizationId },
      ],
    }),

    cancelOrgSubscription: builder.mutation<unknown, CancelSubscriptionArgs>({
      query: ({ subscriptionId, reason, stepUpToken, stepUpTimestamp }) => ({
        url: `/admin/billing/subscriptions/${subscriptionId}`,
        method: 'DELETE',
        body: reason !== undefined ? { reason } : undefined,
        headers: stepUpToken ? { 'X-Step-Up-Token': stepUpToken, ...(stepUpTimestamp && { 'X-Step-Up-Timestamp': stepUpTimestamp }) } : undefined,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgSubscriptions', id: orgId },
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),

    pauseOrgSubscription: builder.mutation<unknown, TogglePauseSubscriptionArgs>({
      query: ({ subscriptionId }) => ({
        url: `/admin/billing/subscriptions/${subscriptionId}/pause`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgSubscriptions', id: orgId },
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),

    unpauseOrgSubscription: builder.mutation<unknown, TogglePauseSubscriptionArgs>({
      query: ({ subscriptionId }) => ({
        url: `/admin/billing/subscriptions/${subscriptionId}/unpause`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgSubscriptions', id: orgId },
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),
  }),
});

export const {
  useGetOrgProjectedBillingQuery,
  useGetOrgBillingAccountQuery,
  useGetOrgSubscriptionsQuery,
  useGetAvailablePackagesQuery,
  useGetOrgUsageSummaryQuery,
  usePauseOrgChargingMutation,
  useResumeOrgChargingMutation,
  useToggleOrgChargingMutation,
  useAddOrgSubscriptionMutation,
  useCancelOrgSubscriptionMutation,
  usePauseOrgSubscriptionMutation,
  useUnpauseOrgSubscriptionMutation,
} = billingDetailApi;
